import { Injectable } from '@angular/core';
import type { DeadlineFormInterface } from '@zaoblako/shared/common/dtos/deadline/deadline-form.dto';
import type { PlanningUnitFormInterface } from '@zaoblako/shared/common/dtos/planning-unit/planning-unit-form.dto';
import { DeadlineTypeEnum } from '@zaoblako/shared/common/enums/deadline.type.enum';
import { TemplateEntityPeriodicityEnum } from '@zaoblako/shared/common/enums/template.entity.periodicity.enum';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { TaskAnalyticsInterface } from '@zaoblako/shared/modules/task/dtos/analytics/task-analytics.dto';
import type { TaskAnalyticsQueryInterface } from '@zaoblako/shared/modules/task/dtos/analytics/task-analytics-query.dto';
import type { ApproverRefuseFormInterface } from '@zaoblako/shared/modules/task/dtos/auditors-and-approvers/approver.refuse.form.dto';
import type { TenantAuditorsInterface } from '@zaoblako/shared/modules/task/dtos/auditors-and-approvers/tenant.auditors.dto';
import type { TenantAuditorsUpdateInterface } from '@zaoblako/shared/modules/task/dtos/auditors-and-approvers/tenant.auditors.update.dto';
import type { TaskExpenseDynamicsQueryInterface } from '@zaoblako/shared/modules/task/dtos/expense-dynamics/task.expense.dynamics.query.dto';
import type { TaskExpenseDynamicsReportInterface } from '@zaoblako/shared/modules/task/dtos/expense-dynamics/task.expense.dynamics.report.dto';
import type { BrsTasksWidgetQueryInterface } from '@zaoblako/shared/modules/task/dtos/brs-tasks-widget/brs-tasks-widget-query.dto';
import type { BrsTasksWidgetFindInterface } from '@zaoblako/shared/modules/task/dtos/brs-tasks-widget/brs-tasks-widget-find.dto';
import type { ExportOneTo1cResultInterface } from '@zaoblako/shared/modules/task/dtos/export-to-1c/export.one.to.1c.result.dto';
import type { TaskHistoryFindInterface } from '@zaoblako/shared/modules/task/dtos/history/task.history.find.dto';
import type { TaskStatusFindInterface } from '@zaoblako/shared/modules/task/dtos/status/task-status-find.dto';
import type { TaskFileFindInterface } from '@zaoblako/shared/modules/task/dtos/task-file-find.dto';
import type { TaskFindInterface } from '@zaoblako/shared/modules/task/dtos/task-find.dto';
import type { TaskMovePaymentDateInterface } from '@zaoblako/shared/modules/task/dtos/task-move-payment-date.dto';
import type { TaskFindQueryInterface } from '@zaoblako/shared/modules/task/dtos/task-find-query.dto';
import type { TaskFormInterface } from '@zaoblako/shared/modules/task/dtos/task-form.dto';
import type { TaskMultipleMovePaymentDateInterface } from '@zaoblako/shared/modules/task/dtos/task-multiple-move-payment-date.dto';
import type { TaskSetStatusInterface } from '@zaoblako/shared/modules/task/dtos/task-set-status.dto';
import type { TasksPageInterface } from '@zaoblako/shared/modules/task/dtos/tasks-page.dto';
import type { TaskTemplateFormInterface } from '@zaoblako/shared/modules/task/dtos/template/task-template-form.dto';
import { TaskStatusTypeEnum } from '@zaoblako/shared/modules/task/enum/task.status.type.enum';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { TaskEditPermissions } from '@zaoblako/web/core/types/task-edit-permissions.type';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TaskService extends BaseService {
  protected baseUrl = '/finance/brs/task';

  public constructor(private readonly authService: AuthService) {
    super();
  }

  public findMany(options: TaskFindQueryInterface): Observable<TasksPageInterface> {
    return this.http.get<TasksPageInterface>(this.baseUrl, {
      params: options ? this.objectToHttpParams(options) : {}
    });
  }

  public createTask(body: TaskFormInterface): Observable<TaskFindInterface> {
    return this.http.post<TaskFindInterface>(this.baseUrl, body);
  }

  public getFiles(taskId: number): Observable<TaskFileFindInterface[]> {
    return this.http.get<TaskFileFindInterface[]>(`${this.baseUrl}/${taskId}/file`);
  }

  public getDefaultAuditors(): Observable<TenantAuditorsInterface> {
    return this.http.get<TenantAuditorsInterface>(`${this.baseUrl}/default-auditors`);
  }

  public setDefaultAuditors(body: TenantAuditorsUpdateInterface): Observable<TenantAuditorsInterface> {
    return this.http.put<TenantAuditorsInterface>(`${this.baseUrl}/default-auditors`, body);
  }

  public setApprove(taskId: number, approverId: number): Observable<TaskStatusFindInterface> {
    return this.http.post<TaskStatusFindInterface>(`${this.baseUrl}/${taskId}/approve/${approverId}`, {});
  }

  public setRefuse(
    taskId: number,
    approverId: number,
    body: ApproverRefuseFormInterface
  ): Observable<TaskStatusFindInterface> {
    return this.http.post<TaskStatusFindInterface>(`${this.baseUrl}/${taskId}/refuse/${approverId}`, body);
  }

  public getHistory(taskId: number): Observable<TaskHistoryFindInterface[]> {
    return this.http.get<TaskHistoryFindInterface[]>(`${this.baseUrl}/${taskId}/history`);
  }

  public getMembers(taskId: number): Observable<UserFindInterface[]> {
    return this.http.get<UserFindInterface[]>(`${this.baseUrl}/${taskId}/participants`);
  }

  public exportOneTo1C(taskId: number): Observable<ExportOneTo1cResultInterface> {
    return this.http.post<ExportOneTo1cResultInterface>(`${this.baseUrl}/${taskId}/export-to-1c`, {});
  }

  public getAnalytics(params: TaskAnalyticsQueryInterface): Observable<TaskAnalyticsInterface> {
    return this.http.get<TaskAnalyticsInterface>(`${this.baseUrl}/analytics`, {
      params: this.objectToHttpParams(params)
    });
  }

  public expenseDynamics(
    taskId: number,
    queryParams: TaskExpenseDynamicsQueryInterface
  ): Observable<TaskExpenseDynamicsReportInterface> {
    return this.http.get<TaskExpenseDynamicsReportInterface>(`${this.baseUrl}/${taskId}/expense-dynamics`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public paymentTasksWidget(queryParams: BrsTasksWidgetQueryInterface): Observable<BrsTasksWidgetFindInterface> {
    return this.http.get<BrsTasksWidgetFindInterface>(`${this.baseUrl}/widget`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getTaskPermissions(task: TaskFindInterface): { editable: boolean; permissions: TaskEditPermissions } {
    const isDraft = task?.status?.type === TaskStatusTypeEnum.DRAFT;
    const isScheduled = task?.status?.type === TaskStatusTypeEnum.SCHEDULED;
    const isOnApproval = task?.status?.type === TaskStatusTypeEnum.ON_APPROVAL;
    const isAgreed = task?.status?.type === TaskStatusTypeEnum.AGREED;
    const isCurrentUserTask =
      this.authService.currentUser.id === task?.createdBy?.id ||
      this.authService.currentUser.id === task?.responsible?.id ||
      task.canEdit;

    const permissions: TaskEditPermissions = {
      responsible: isCurrentUserTask && (isDraft || isScheduled),
      contragent: isCurrentUserTask && (isDraft || isScheduled),
      organization: isCurrentUserTask && (isDraft || isScheduled || isOnApproval),
      filial: isCurrentUserTask && (isDraft || isScheduled),
      unit: isCurrentUserTask && (isDraft || isScheduled),
      direction: isCurrentUserTask && (isDraft || isScheduled),
      type: isCurrentUserTask && (isDraft || isScheduled || isOnApproval),
      priority: isCurrentUserTask && (isDraft || isScheduled),
      subject: isCurrentUserTask && (isDraft || isScheduled || isAgreed),
      date: isCurrentUserTask && (isDraft || isScheduled || isAgreed),
      status: false,
      amount: isCurrentUserTask && (isDraft || isScheduled),
      nds: isCurrentUserTask && (isDraft || isScheduled),
      scheme: isCurrentUserTask && (isDraft || isScheduled),
      userAuditors: isCurrentUserTask && (isDraft || isScheduled),
      userGroupAuditors: isCurrentUserTask && (isDraft || isScheduled),
      orders: isCurrentUserTask && (isDraft || isScheduled),
      deal: isCurrentUserTask && (isDraft || isScheduled),
      costCenter: isCurrentUserTask && (isDraft || isScheduled),
      vat: isCurrentUserTask && (isDraft || isScheduled),
      budgetClassificactionCode: isCurrentUserTask && (isDraft || isScheduled),
      municipalityClassificationCode: isCurrentUserTask && (isDraft || isScheduled),
      payerStatus: isCurrentUserTask && (isDraft || isScheduled),
      uniqueAccrualIdentifier: isCurrentUserTask && (isDraft || isScheduled),
      paymentReason: isCurrentUserTask && (isDraft || isScheduled),
      taxPeriod: isCurrentUserTask && (isDraft || isScheduled),
      documentNumber: isCurrentUserTask && (isDraft || isScheduled),
      documentDate: isCurrentUserTask && (isDraft || isScheduled),
      currency: isCurrentUserTask && (isDraft || isScheduled)
    };

    const editable = Object.keys(permissions).some((prop) => permissions[prop]);

    return { editable, permissions };
  }

  public getTemplateFromTask(taskId: number): Observable<TaskTemplateFormInterface> {
    return this.findById<TaskFindInterface>(taskId).pipe(
      map(
        (task) =>
          ({
            status: task.status,
            creditPayment: task.creditPayment,
            contragent: task.contragent,
            organization: task.organization,
            filial: task.filial,
            unit: task.unit,
            direction: task.direction,
            type: task.type,
            priority: task.priority,
            subject: task.subject,
            amount: task.amount,
            currency: task.currency,
            nds: task.nds,
            deal: task.deal,
            costCenter: task.costCenter,
            approvers: task.approvers?.map((approver) => {
              return approver;
            }),
            planning: {
              ...(<PlanningUnitFormInterface>{}),
              periodicity: TemplateEntityPeriodicityEnum.NONE
            },
            deadline: {
              ...(<DeadlineFormInterface>{}),
              daysAfter: 0,
              type: DeadlineTypeEnum.RELATIVE
            },
            userAuditors: task.userAuditors,
            userGroupAuditors: task.userGroupAuditors,
            scheme: task.scheme,
            orders: task.orders?.map((order) => {
              delete order.id;
              return order;
            })
          } as unknown as TaskTemplateFormInterface)
      )
    );
  }

  public setStatus(id: number, body: TaskSetStatusInterface): Observable<TaskFindInterface> {
    return this.http.post<TaskFindInterface>(`${this.baseUrl}/${id}/update-status`, body);
  }

  public movePaymentDate(id: number, body: TaskMovePaymentDateInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/move-payment-date`, body);
  }

  public moveMultiplePaymentDate(body: TaskMultipleMovePaymentDateInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/multiple-move-payment-date`, body);
  }
}
